import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { API, api } from "../lib/api";
import { ArchWindow } from "./ArchWindow";

const SESSION_KEY = "dm_chat_session";
const getSession = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(SESSION_KEY, id); }
  return id;
};

const WELCOME = "Hi, I'm Luke's Mission Assistant. Ask me anything about Digital Missions: what I build for churches, how to apply, or where your gift goes.";

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
      m.role === "user" ? "bg-[#E84C4C] text-white rounded-br-md" : "bg-[#FFF5F2] text-[#120716]/85 border border-[#120716]/8 rounded-bl-md"
    }`}>
      {m.content || <span className="inline-flex gap-1"><span className="chat-dot" /><span className="chat-dot" /><span className="chat-dot" /></span>}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    if (!open || sessionId.current) return;
    sessionId.current = getSession();
    api.get(`/chat/${sessionId.current}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    const append = (fn) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: fn(c[c.length - 1].content) }; return c; });
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || "Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) append((c) => c + evt.delta);
          if (evt.error) append(() => evt.error);
        }
      }
    } catch (err) {
      append(() => typeof err.message === "string" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close assistant" : "Open Mission Assistant"}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white flex items-center justify-center shadow-[0_12px_36px_rgba(232,76,76,0.45)] transition-all duration-300 hover:-translate-y-0.5"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[min(400px,calc(100vw-3rem))] h-[min(560px,calc(100vh-8rem))] rounded-2xl bg-white border border-[#120716]/10 shadow-[0_24px_80px_rgba(18,7,22,0.18)] flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#120716]/10 bg-[#FFF5F2]">
              <ArchWindow size={18} bar="#FFF5F2" />
              <div className="leading-tight">
                <div className="font-orbitron text-xs tracking-[0.3em] text-[#120716]">MISSION ASSISTANT</div>
                <div className="text-[11px] text-[#120716]/50">Powered by Claude · answers in Luke's voice</div>
              </div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              <Bubble m={{ role: "assistant", content: WELCOME }} i="welcome" />
              {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            </div>
            <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-[#120716]/10">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about applying, services, giving..."
                maxLength={1500}
                className="flex-1 rounded-full bg-[#FFF5F2] border border-[#120716]/10 px-4 py-2.5 text-sm text-[#120716] placeholder:text-[#120716]/35 focus:outline-none focus:border-[#E84C4C]"
              />
              <button data-testid="chat-send-btn" disabled={busy || !input.trim()} aria-label="Send" className="w-10 h-10 rounded-full bg-[#E84C4C] disabled:opacity-40 text-white flex items-center justify-center transition-all">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
